// Account Manager Class for the Account Section
// Handles business logic and state management for the user account page
// Follows the Manager and Component pattern adapted from AgentGroupChat

import { Manager } from "../../../core";
import { logger } from "../../../core";
import { navigationManager } from "../../../managers/navigation-manager";

export class AccountManager extends Manager {
  constructor() {
    super();
    this.user = null;
    this.subscriptionStatus = null;
    this.isLoading = true;
    this.isInitialized = false;
  }

  /**
   * Initialize global variables and setup
   * In React: Sets up navigation manager connection
   */
  async initGlobalVariables() {
    // Navigation manager will be connected via the React hook adapter
    // The navigate function is set at the React level
  }

  /**
   * Initialize states and data
   * In React: Load user data and subscription status
   */
  async initStates() {
    logger.log(
      {
        tags: "manager|account|init",
        color1: "cyan",
      },
      "Initializing AccountSection manager states"
    );

    try {
      // Load user data and subscription status
      await Promise.all([
        this.loadUserData(),
        this.loadSubscriptionStatus()
      ]);

      this.isInitialized = true;

      logger.log(
        {
          tags: "manager|account|ready",
          color1: "lime",
        },
        "AccountSection manager states initialized successfully"
      );
    } catch (error) {
      logger.error(
        {
          tags: "manager|account|error",
          color1: "red",
          color2: "orange",
          includeSource: true,
        },
        "Failed to initialize AccountSection manager states:",
        error
      );

      throw error;
    }
  }

  async loadUserData() {
    // This will be handled by the AuthContext in the React component
    // For now, just simulate loading
    await new Promise((resolve) => setTimeout(resolve, 100));
  }

  async loadSubscriptionStatus() {
    // This will be fetched via API in the React component
    // For now, just simulate loading
    await new Promise((resolve) => setTimeout(resolve, 100));
  }

  /**
   * Fetch usage values for the Account plan bars (credits/apps/storage).
   * Returns backend shape: { membershipDetails, credits, apps, storage }.
   */
  async fetchAccountUsage() {
    return await this.handleAsync(async () => {
      const response = await fetch('/api/account/usage', {
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok || !data?.success) {
        throw new Error(data?.error || 'Failed to fetch account usage');
      }

      return data.usage || null;
    }, {
      showLogs: false,
    });
  }

  /**
   * Handle upgrade button click
   * Navigates user to pricing section
   */
  handleUpgrade() {
    logger.log(
      {
        tags: "manager|account|action",
        color: "cyan",
      },
      "Upgrade button clicked - navigating to pricing"
    );

    navigationManager.navigateTo("pricing");
  }

  /**
   * Handle manage subscription button click
   * Opens Stripe customer portal
   */
  async handleManageSubscription() {
    logger.log(
      {
        tags: "manager|account|action",
        color: "cyan",
      },
      "Manage subscription clicked - opening customer portal"
    );

    try {
      const response = await fetch('/api/stripe/customer-portal', {
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data?.error || 'Failed to open customer portal');
      }

      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('Customer portal URL not returned');
      }
    } catch (error) {
      logger.error(
        {
          tags: "manager|account|error",
          color1: "red",
        },
        "Customer portal error:",
        error
      );
      alert(`Error opening customer portal: ${error.message || 'Unknown error'}`);
    }
  }

  /**
   * Handle download button click
   * Navigates to downloads section
   */
  handleDownload() {
    logger.log(
      {
        tags: "manager|account|action",
        color: "cyan",
      },
      "Download button clicked - navigating to downloads"
    );

    navigationManager.navigateTo("downloads");
  }

  /**
   * Navigate to a section from quick links
   */
  navigateToSection(section) {
    logger.log(
      {
        tags: "manager|account|navigation",
        color: "cyan",
      },
      `Navigating to ${section} section`
    );

    navigationManager.navigateTo(section);
  }

  /**
   * Check whether an email can receive this transfer (existing account, not self, no local-revolt tag yet, no prior pair transfer).
   * @returns {Promise<{ success: boolean, valid?: boolean, code?: 'self'|'not_found'|'already_has_local'|'already_transferred_pair'|'invalid_email', error?: string, senderTransferPhase?: 'before_claim'|'after_claim', existingPairTransfer?: { transferId: string, createdAt: string|null } }>}
   */
  async checkTransferRecipient(email) {
    const response = await fetch("/api/account/transfer/lookup-recipient", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: String(email ?? "").trim() }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data?.success) {
      return {
        success: false,
        error: data?.error || "lookup_failed",
      };
    }
    return {
      success: true,
      valid: Boolean(data.valid),
      code: data.code,
      senderTransferPhase: data.senderTransferPhase,
      existingPairTransfer: data.existingPairTransfer,
    };
  }

  /**
   * Markdown for the Gumroad UE plugin transfer policy modal (legal/transfer-policies/gumroad-revolt-unreal-engine-plugin).
   */
  async fetchTransferPolicyMarkdown() {
    const response = await fetch("/api/account/transfer/policy-markdown", {
      credentials: "include",
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data?.success || typeof data.markdown !== "string") {
      throw new Error(data?.error || "Failed to load transfer policy");
    }
    return data.markdown;
  }

  /**
   * Record acceptance of legal documents at their current required versions.
   * @param {{ slugs?: string[], acceptAllPending?: boolean }} options
   * @returns {Promise<object>} legalAcceptance summary from API
   */
  async acceptLegalDocuments(options = {}) {
    const response = await fetch("/api/account/legal/accept", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        slugs: Array.isArray(options.slugs) ? options.slugs : [],
        acceptAllPending: options.acceptAllPending === true,
      }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data?.success) {
      throw new Error(data?.error || "Failed to record legal acceptance");
    }
    return data.legalAcceptance ?? null;
  }

  /**
   * Gumroad UE plugin transfer: runs server-side eligibility (same as lookup) and either
   * simulates (testMode, admin) or returns validation result / future execution.
   */
  async executeGumroadUePluginTransfer({ recipientEmail, testMode, sourceId }) {
    const response = await fetch(
      "/api/account/transfer/gumroad-ue-plugin/execute",
      {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          recipientEmail: String(recipientEmail ?? "").trim(),
          testMode: Boolean(testMode),
          sourceId: sourceId ?? "gumroad_revolt_unreal_engine_plugin",
        }),
      }
    );
    const data = await response.json().catch(() => ({}));
    return {
      ok: response.ok,
      status: response.status,
      data,
    };
  }

  /**
   * Gumroad bundle promo: eligibility, existing code, and marketplace link metadata.
   * @returns {Promise<object>}
   */
  async fetchGumroadBundlePromoStatus() {
    const response = await fetch("/api/account/gumroad/bundle-promo/status", {
      credentials: "include",
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data?.success) {
      throw new Error(data?.error || "Failed to load Gumroad bundle promo status");
    }
    return data;
  }

  /**
   * Gumroad bundle promo: request single-use Stripe promotion code when eligible.
   * @returns {Promise<object>}
   */
  async requestGumroadBundlePromo() {
    const response = await fetch("/api/account/gumroad/bundle-promo/request", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
    });
    const data = await response.json().catch(() => ({}));
    return {
      ok: response.ok,
      status: response.status,
      data,
    };
  }

  /**
   * Get the current initialization status
   * Used by React hook adapter
   */
  getIsInitialized() {
    return this.isInitialized;
  }
}
